import {
  DotFillIcon,
  ClockIcon,
  CircleSlashIcon,
  SkipIcon,
  GitMergeIcon,
  GitMergeQueueIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  KebabHorizontalIcon,
  QuestionBubbleIcon,
  ShieldLockIcon,
  type Icon,
} from '@primer/octicons-react'
import {
  type FocusEventHandler,
  type PointerEventHandler,
  type RefObject,
  useCallback,
  useId,
  useImperativeHandle,
  useState,
} from 'react'
import {WorkingAsciiLoader} from '@github-ui/agent-sessions-interface/components/WorkingAsciiLoader'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCurrentUserOptional} from '../../../contexts/CurrentUserContext'
import {useArchiveDialogOptional} from '../../../contexts/ArchiveDialogContext'
import {TaskActions} from '../../TaskActions'
import {RenameTaskDialog} from '../../RenameTaskDialog'
import {isQuickChatTask} from '../../../utils/is-quick-chat-task'
import {AnchoredOverlay, NavList, VisuallyHidden, asSlot} from '@primer/react'
import {useSidebarItemHovercard} from '@github-ui/copilot-shared-ui/hooks/use-sidebar-item-hovercard'
import {useResolvedTaskTitle} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import hovercardStyles from '@github-ui/copilot-shared-ui/components/SidebarItemHovercard.module.css'
import {clsx} from 'clsx'
import {getTaskTitle} from '../../../utils/task-helpers'
import {getEffectivePrState, resolveEffectiveBranchName} from '../../../utils/task-artifact-helpers'
import {
  getLeadingVisualIconAttributes,
  getLeadingVisualStatusLabel,
} from '../../../utils/get-leading-visual-icon-attributes'
import {resolveAgentProfileForTask} from '../../../agents/resolve-agent-profile'
import {getReadableSessionState, normalizeSessionStatusLabel, SessionState} from '../../../types/session'
import {SessionsNavItemHovercard} from './SessionsNavItemHovercard'
import styles from './SessionsNavItem.module.css'
import sharedStyles from './SessionsByRepoNav.module.css'
import trailingVisualStyles from '../../AgentTypeTrailingVisual.module.css'
import type {SessionsNavItemProps} from './types'

// The CSS-anchor path can briefly render at (0, 0) before the NavList anchor resolves.
const TASK_HOVERCARD_POSITIONING_SETTINGS = {disable: true} as const

function getSessionLeadingVisual(
  state: SessionState | undefined,
  statusLabel?: string,
  agentSlug?: SessionsNavItemProps['task']['agentSlug'],
) {
  if (state === SessionState.InProgress) {
    return <WorkingAsciiLoader ariaLabel={statusLabel ? undefined : 'Working'} className={styles.loadingStatus} />
  }

  if (state === SessionState.Queued) {
    return <ClockIcon className="fgColor-attention" aria-label={statusLabel ? undefined : 'Queued'} />
  }

  if (state === SessionState.Failed || state === SessionState.TimedOut) {
    return <SkipIcon className="fgColor-danger" aria-label="Failed" />
  }

  if (state === SessionState.Cancelled && isCopilotCodingAgent(agentSlug)) {
    return <CircleSlashIcon className="fgColor-muted" aria-label="Cancelled" />
  }

  if (state === SessionState.WaitingForUser) {
    return <QuestionBubbleIcon className="fgColor-attention" aria-label="Needs attention" />
  }

  return <DotFillIcon className={styles.neutralDot} aria-label={state ? getReadableSessionState(state) : 'Unknown'} />
}

interface BranchStatusVisualAttributes {
  icon: Icon
  color: string
  label: string
  testId: string
}

function getBranchStatusVisualAttributes(task: SessionsNavItemProps['task']): BranchStatusVisualAttributes | undefined {
  const branchName = resolveEffectiveBranchName(undefined, task, task.pull)
  const prState = getEffectivePrState(task.artifact_state, task.pull)

  if (task.pull?.inMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)',
      label: branchName ? `${branchName}, in merge queue` : 'In merge queue',
      testId: 'branch-status-merge-queue',
    }
  }

  switch (prState) {
    case 'open':
      return {
        icon: GitPullRequestIcon,
        color: 'var(--fgColor-open)',
        label: branchName ? `${branchName}, open pull request` : 'Open pull request',
        testId: 'branch-status-open',
      }
    case 'draft':
      return {
        icon: GitPullRequestDraftIcon,
        color: 'var(--fgColor-draft)',
        label: branchName ? `${branchName}, draft pull request` : 'Draft pull request',
        testId: 'branch-status-draft',
      }
    case 'merged':
      return {
        icon: GitMergeIcon,
        color: 'var(--fgColor-done)',
        label: branchName ? `${branchName}, merged` : 'Merged pull request',
        testId: 'branch-status-merged',
      }
    case 'closed':
      return {
        icon: GitPullRequestClosedIcon,
        color: 'var(--fgColor-closed)',
        label: branchName ? `${branchName}, closed pull request` : 'Closed pull request',
        testId: 'branch-status-closed',
      }
    default:
      return undefined
  }
}

function SessionBranchStatusVisual({attributes}: {attributes: BranchStatusVisualAttributes}) {
  const BranchStatusIcon = attributes.icon

  return (
    <span className={trailingVisualStyles.wrapper} style={{color: attributes.color}} data-testid={attributes.testId}>
      <BranchStatusIcon />
      <VisuallyHidden>{attributes.label}</VisuallyHidden>
    </span>
  )
}

interface SessionNavItemActionsProps {
  task: SessionsNavItemProps['task']
  anchorRef: RefObject<HTMLButtonElement | null>
  onOpenChange: (open: boolean) => void
  extraItems?: SessionsNavItemProps['extraItems']
  currentUserId?: number
  onPointerEnter: () => void
  onPointerLeave: PointerEventHandler<HTMLElement>
  onFocus: FocusEventHandler<HTMLElement>
  onBlur: FocusEventHandler<HTMLElement>
  onRename?: () => void
  enablePinning?: boolean
  titleId?: string
}

const SessionNavItemActions = asSlot(function SessionNavItemActionsImpl({
  task,
  anchorRef,
  onOpenChange,
  extraItems,
  currentUserId,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
  onRename,
  enablePinning,
  titleId,
}: SessionNavItemActionsProps) {
  const currentUser = useCurrentUserOptional()
  const agentProfile = resolveAgentProfileForTask(task)
  const [repoOwner, repoName] = task.nwo?.split('/', 2) ?? []
  const effectiveCurrentUserId = currentUserId ?? currentUser?.currentUser?.id
  const currentUserIsOwner = effectiveCurrentUserId != null && task.creator_id === effectiveCurrentUserId
  const actionLabel = 'Manage session'

  return (
    <TaskActions
      enablePinning={enablePinning}
      ariaLabel={actionLabel}
      anchorRef={anchorRef}
      onOpenChange={onOpenChange}
      extraItems={extraItems}
      anchor={
        <NavList.TrailingAction
          data-sidebar-pin-task-id={enablePinning ? task.id : undefined}
          aria-describedby={titleId}
          icon={KebabHorizontalIcon}
          label={actionLabel}
          tooltipDirection="sw"
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      }
      state={task.latest_session_state}
      pull={task.pull}
      taskId={task.id}
      repoOwner={repoOwner}
      repoName={repoName}
      currentUserIsOwner={currentUserIsOwner}
      hideArchive={isQuickChatTask(task) && !currentUserIsOwner}
      archivedAt={task.archived_at}
      eventType={task.event_type}
      eventIdentifiers={task.event_identifiers}
      interactiveSession={agentProfile.interactiveSession}
      agentSlug={agentProfile.slug}
      hideCopy={agentProfile.privateSessions}
      hideShare={!agentProfile.privateSessions}
      sharingStatus={task.sharing_status}
      automationId={task.automation_id}
      isQuickChatTask={isQuickChatTask(task)}
      onRename={onRename}
    />
  )
}, NavList.TrailingAction)

export const SessionsNavItem = ({
  task,
  href,
  unavailableReason,
  statusLabel,
  currentUserId,
  isSelected,
  onClick,
  preferPullTitle,
  formatAsReview,
  extraItems,
  actionsRef,
  onRename,
  environmentStatusApiURL,
  readOnly,
  enablePinning,
  ssoRequired,
  chatTypeContextProvidedByGroup,
}: SessionsNavItemProps) => {
  const [isRenaming, setIsRenaming] = useState(false)
  const taskActions = useArchiveDialogOptional()
  const {
    open,
    anchorRef,
    anchorRegionProps,
    anchorProps,
    actionRef,
    onActionMenuOpenChange,
    contentProps,
    noReturnFocusRef,
    cancelPendingOpen,
    closeNow,
  } = useSidebarItemHovercard()
  useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(actionsRef, () => actionRef.current)
  const popoverId = useId()
  const statusId = useId()
  const titleId = useId()
  const isUnavailable = unavailableReason !== undefined
  const hovercardOpen = open && !isUnavailable && !isRenaming && !readOnly
  const isChat = isQuickChatTask(task)
  const useSessionLeadingVisual =
    !isChat ||
    task.latest_session_state === SessionState.InProgress ||
    task.latest_session_state === SessionState.Queued ||
    task.latest_session_state === SessionState.Completed ||
    task.latest_session_state === SessionState.Idle
  const leadingVisualAttributes = !useSessionLeadingVisual
    ? getLeadingVisualIconAttributes(
        getEffectivePrState(task.artifact_state, task.pull),
        task.pull?.inMergeQueue ?? false,
        task.latest_session_state,
        !!task.archived_at,
        task.agentSlug,
      )
    : undefined
  const StateIcon = leadingVisualAttributes?.icon
  const accessibleStatusLabel =
    (statusLabel ? normalizeSessionStatusLabel(statusLabel) : undefined) ||
    (StateIcon
      ? getLeadingVisualStatusLabel(
          getEffectivePrState(task.artifact_state, task.pull),
          task.pull?.inMergeQueue ?? false,
          task.latest_session_state,
          !!task.archived_at,
        )
      : undefined)
  const branchStatusVisualAttributes = isChat ? undefined : getBranchStatusVisualAttributes(task)
  const showTrailingVisual = ssoRequired || branchStatusVisualAttributes !== undefined
  const title = useResolvedTaskTitle(task.id, getTaskTitle(task, task.pull?.title, {preferPullTitle, formatAsReview}))
  const handleStartRename = useCallback(() => {
    closeNow()
    if (onRename) {
      onRename(actionRef)
    } else {
      setIsRenaming(true)
    }
    sendEvent('agent_sessions.task_actions', {target: 'RENAME_TASK_START', task_id: task.id})
  }, [actionRef, closeNow, onRename, task.id])
  const handleCloseRename = useCallback(() => setIsRenaming(false), [])
  const [repoOwner, repoName] = task.nwo?.split('/', 2) ?? []

  return (
    <>
      <NavList.Item
        data-sidebar-pin-task-id={enablePinning ? task.id : undefined}
        as={isUnavailable ? 'button' : 'a'}
        href={isUnavailable ? undefined : href}
        type={isUnavailable ? 'button' : undefined}
        aria-disabled={isUnavailable || undefined}
        aria-current={!isUnavailable && isSelected ? 'page' : undefined}
        className={clsx(styles.sessionNavItem)}
        {...(isUnavailable || readOnly ? {} : anchorRegionProps)}
        {...(isUnavailable || readOnly ? {} : anchorProps)}
        onPointerDown={isUnavailable || readOnly ? undefined : cancelPendingOpen}
        onClick={isUnavailable ? undefined : onClick}
        {...(!isUnavailable && {
          'aria-describedby': hovercardOpen ? popoverId : accessibleStatusLabel ? statusId : undefined,
        })}
      >
        <NavList.LeadingVisual
          style={leadingVisualAttributes ? {color: leadingVisualAttributes.color} : undefined}
          data-testid={leadingVisualAttributes?.['data-testid']}
        >
          {StateIcon ? <StateIcon /> : getSessionLeadingVisual(task.latest_session_state, statusLabel, task.agentSlug)}
          {accessibleStatusLabel && <VisuallyHidden id={statusId}>{accessibleStatusLabel}</VisuallyHidden>}
        </NavList.LeadingVisual>
        <span id={enablePinning ? titleId : undefined} className={clsx(sharedStyles.truncate, styles.taskLabel)}>
          {title}
        </span>
        {isChat && !chatTypeContextProvidedByGroup && <VisuallyHidden>Chat session</VisuallyHidden>}
        {isUnavailable && <NavList.Description variant="block">{unavailableReason}</NavList.Description>}
        {showTrailingVisual && (
          <NavList.TrailingVisual
            className={clsx(styles.sessionTrailingVisual, readOnly && styles.readOnlyTrailingVisual)}
          >
            {!ssoRequired && branchStatusVisualAttributes && (
              <SessionBranchStatusVisual attributes={branchStatusVisualAttributes} />
            )}
            {ssoRequired && (
              <>
                <ShieldLockIcon />
                <VisuallyHidden>SSO required</VisuallyHidden>
              </>
            )}
          </NavList.TrailingVisual>
        )}
        {task.id && !readOnly ? (
          <SessionNavItemActions
            enablePinning={enablePinning}
            titleId={enablePinning ? titleId : undefined}
            task={task}
            anchorRef={actionRef}
            onOpenChange={onActionMenuOpenChange}
            extraItems={extraItems}
            currentUserId={currentUserId}
            onPointerEnter={anchorRegionProps.onPointerEnter}
            onPointerLeave={anchorRegionProps.onPointerLeave}
            onFocus={anchorProps.onFocus}
            onBlur={anchorProps.onBlur}
            onRename={isQuickChatTask(task) && !taskActions?.chatApiUrl ? undefined : handleStartRename}
          />
        ) : null}
      </NavList.Item>
      {hovercardOpen && (
        <AnchoredOverlay
          open={open}
          anchorRef={anchorRef}
          renderAnchor={null}
          onClose={closeNow}
          focusTrapSettings={{disabled: true}}
          focusZoneSettings={{disabled: true}}
          overlayProps={{preventFocusOnOpen: true, returnFocusRef: noReturnFocusRef}}
          cssAnchorPositioningSettings={TASK_HOVERCARD_POSITIONING_SETTINGS}
          side="outside-right"
          align="start"
          displayInViewport
        >
          <div
            {...contentProps}
            id={popoverId}
            role="tooltip"
            data-testid="session-hovercard"
            className={clsx(hovercardStyles.hovercard, styles.hovercard)}
          >
            <SessionsNavItemHovercard
              task={task}
              preferPullTitle={preferPullTitle}
              formatAsReview={formatAsReview}
              statusLabel={statusLabel}
              environmentStatusApiURL={environmentStatusApiURL}
            />
          </div>
        </AnchoredOverlay>
      )}
      {isRenaming && !readOnly && (
        <RenameTaskDialog
          title={isQuickChatTask(task) ? 'Rename chat' : 'Rename task'}
          currentName={title}
          taskId={task.id}
          repoOwner={repoOwner}
          repoName={repoName}
          apiUrl={taskActions?.chatApiUrl}
          onRenamed={taskActions?.onTaskStateChange}
          onClose={handleCloseRename}
          returnFocusRef={actionRef}
        />
      )}
    </>
  )
}
